import type { JSX } from "preact";
import { useRef, useState } from "preact/hooks";
import {
  allowRemoteContent,
  closeEmlPreview,
  deleteMessage,
  moveMessage,
  openComposeFor,
  openEmlPreview,
  toggleFlagged,
  toggleSeen,
} from "../actions";
import {
  emlPreview,
  emlPreviewError,
  emlPreviewLoading,
  fullMessage,
  mailboxes,
  messageError,
  messageLoading,
  remoteContentDefault,
  selectedUid,
  showRemote,
} from "../state";
import type { MailAddress } from "../../shared/types";
import { isEmlAttachment } from "../../shared/eml";
import { avatarColor, displayName, faviconUrl, formatListDate, initials, shouldShowRemoteBanner } from "../util";
import { Icon } from "./icons";
import { api } from "../api";

function SenderAvatar({ address, label, size }: { address: string; label: string; size: number }): JSX.Element {
  const [imgOk, setImgOk] = useState(true);
  const url = faviconUrl(address);

  if (url && imgOk) {
    return (
      <img
        src={url}
        alt=""
        width={size}
        height={size}
        class="h-10 w-10 shrink-0 rounded-full object-contain bg-ink-3 p-1"
        onError={() => setImgOk(false)}
      />
    );
  }
  return (
    <span
      class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-fg-on-accent"
      style={`background:${avatarColor(label)}`}
    >
      {initials(label)}
    </span>
  );
}

export function Reader(): JSX.Element {
  if (selectedUid.value == null) return <EmptyReader />;

  const m = fullMessage.value;
  const loading = messageLoading.value;
  const visibleAttachments = m ? m.attachments.filter((a) => !a.inline) : [];

  const archiveMbox = mailboxes.value.find((mb) => mb.specialUse === "archive");
  const trashMbox = mailboxes.value.find((mb) => mb.specialUse === "trash");
  const archivePath = archiveMbox?.path ?? "Archive";
  const trashPath = trashMbox?.path ?? "Trash";

  return (
    <section class="flex min-h-0 min-w-0 flex-1 flex-col bg-ink-0">
      <div class="flex h-12 shrink-0 items-center gap-1 border-b border-subtle px-3">
        <button
          class="icon-btn"
          title="Reply"
          disabled={!m}
          onClick={() => openComposeFor("reply")}
        >
          <Icon name="reply" size={18} />
        </button>
        <button
          class="icon-btn"
          title="Reply all"
          disabled={!m}
          onClick={() => openComposeFor("replyAll")}
        >
          <Icon name="replyAll" size={18} />
        </button>
        <button
          class="icon-btn"
          title="Forward"
          disabled={!m}
          onClick={() => openComposeFor("forward")}
        >
          <Icon name="forward" size={18} />
        </button>
        <button
          class="icon-btn"
          title={m?.flagged ? "Unstar" : "Star"}
          disabled={!m}
          onClick={() => m && void toggleFlagged(m.uid)}
        >
          <Icon
            name={m?.flagged ? "starFilled" : "star"}
            size={18}
            class={m?.flagged ? "text-warn" : undefined}
          />
        </button>
        <button
          class="icon-btn"
          title={m?.seen ? "Mark unread" : "Mark read"}
          disabled={!m}
          onClick={() => m && void toggleSeen(m.uid)}
        >
          <Icon name={m?.seen ? "mailOpen" : "inbox"} size={18} />
        </button>
        <button
          class="icon-btn"
          title="Move to Archive"
          disabled={!m}
          onClick={() => m && void moveMessage(m.uid, archivePath)}
        >
          <Icon name="archive" size={18} />
        </button>
        <button
          class="icon-btn"
          title="Move to Trash"
          disabled={!m}
          onClick={() => m && void moveMessage(m.uid, trashPath)}
        >
          <Icon name="trash" size={18} />
        </button>
        <button
          class="icon-btn text-danger"
          title="Delete permanently"
          disabled={!m}
          onClick={() => m && void deleteMessage(m.uid)}
        >
          <Icon name="x" size={18} />
        </button>
        <button class="icon-btn ml-auto" title="More" disabled>
          <Icon name="dots" size={18} />
        </button>
      </div>

      {loading && !m && <ReaderSkeleton />}
      {messageError.value && <div class="p-6 text-sm text-danger">{messageError.value}</div>}

      {m && (
        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <header class="px-6 pt-5">
            <h2 class="text-xl font-semibold text-fg">{m.subject || "(no subject)"}</h2>
            <div class="mt-3 flex items-start gap-3">
              <SenderAvatar address={m.from[0]?.address ?? ""} label={displayName(m.from[0])} size={40} />
              <div class="min-w-0 flex-1 text-sm">
                <div class="flex items-baseline gap-2">
                  <span class="truncate font-medium text-fg">{displayName(m.from[0])}</span>
                  {m.from[0]?.address && (
                    <span class="truncate text-muted">&lt;{m.from[0].address}&gt;</span>
                  )}
                  <span class="ml-auto shrink-0 text-xs text-muted">{formatListDate(m.date)}</span>
                </div>
                <div class="mt-0.5 truncate text-xs text-muted">
                  To: {addrLine(m.to) || "—"}
                  {m.cc.length > 0 && <span>, Cc: {addrLine(m.cc)}</span>}
                </div>
              </div>
            </div>
          </header>

          {shouldShowRemoteBanner(m.hasRemoteContent, showRemote.value, remoteContentDefault.value) && (
            <div class="mx-6 mt-4 flex items-center gap-3 rounded-lg border border-subtle bg-ink-1 px-3 py-2 text-sm">
              <Icon name="mailOpen" size={16} class="text-muted" />
              <span class="text-fg-dim">Remote content was blocked to protect your privacy.</span>
              <button
                onClick={() => allowRemoteContent()}
                class="ml-auto shrink-0 rounded-md bg-accent px-2.5 py-1 text-xs font-semibold text-fg-on-accent hover:bg-accent-600"
              >
                Show content
              </button>
            </div>
          )}

          <div class="px-6 py-4">
            {m.html != null ? (
              <EmailFrame html={m.html} />
            ) : (
              <pre class="whitespace-pre-wrap break-words rounded-lg bg-white p-4 text-sm text-black">
                {m.text || "(empty message)"}
              </pre>
            )}
          </div>

          {visibleAttachments.length > 0 && (
            <div class="border-t border-strong px-6 py-4">
              <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                {visibleAttachments.length} attachment{visibleAttachments.length > 1 ? "s" : ""}
              </div>
              <div class="flex flex-wrap gap-2">
                {visibleAttachments.map((a) => (
                  <div
                    key={a.partId}
                    class="flex items-center gap-1 rounded bg-accent/10 text-xs font-medium text-accent"
                  >
                    <a
                      href={api.attachmentUrl(m.mailbox, m.uid, a.partId)}
                      download={a.filename}
                      title="Download"
                      class="flex min-w-0 items-center gap-1.5 px-2 py-1 hover:bg-accent/20"
                    >
                      <Icon name="download" size={14} />
                      <span class="max-w-[16rem] truncate">{a.filename}</span>
                      {a.size > 0 && <span class="text-muted">({formatBytes(a.size)})</span>}
                    </a>
                    {isEmlAttachment(a.contentType, a.filename) && (
                      <button
                        type="button"
                        title="View message"
                        onClick={() => void openEmlPreview(a.partId)}
                        class="flex shrink-0 items-center gap-1 rounded px-1.5 py-1 hover:bg-accent/20"
                      >
                        <Icon name="mailOpen" size={14} />
                        View
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

/**
 * Renders sanitized email HTML inside a sandboxed iframe.
 * `allow-same-origin` (WITHOUT `allow-scripts`) lets us measure content height
 * for auto-fit while keeping all scripts disabled — a safe display sandbox.
 */
function EmailFrame({ html }: { html: string }): JSX.Element {
  const ref = useRef<HTMLIFrameElement>(null);
  const fit = (): void => {
    const f = ref.current;
    if (!f) return;
    try {
      const doc = f.contentDocument;
      if (doc?.body) {
        // No cap — let the iframe grow to its full content height.
        // The parent div already has overflow-y-auto so the page scroll works.
        f.style.height = `${doc.documentElement.scrollHeight + 16}px`;
      }
    } catch {
      /* cross-origin guard; ignore */
    }
  };
  // Scoped CSP for the sandboxed document itself. `srcdoc` has no CSP of its
  // own by default, so without this it INHERITS the app shell's CSP (per the
  // CSP inheritance rules for local-scheme documents) — whose `img-src`
  // only allows `https:`, silently blocking plain-`http:` remote images even
  // after the user clicks "Show content" and the server restores their `src`.
  // The real security boundary here is the sandbox (no allow-scripts) plus
  // server-side sanitizeEmailHtml, not this CSP, so it's safe to allow image
  // loads broadly, scoped ONLY to this isolated document.
  const srcdoc = `<!doctype html><html><head><meta name="color-scheme" content="light"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src * data: blob:; style-src 'unsafe-inline'; font-src * data:; script-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none';"><base target="_blank"></head><body style="margin:12px;font-family:ui-sans-serif,system-ui,sans-serif;color:#1a1a1a;font-size:14px;line-height:1.5;word-break:break-word;">${html}</body></html>`;
  return (
    <iframe
      ref={ref}
      onLoad={fit}
      title="Message body"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      referrerpolicy="no-referrer"
      srcdoc={srcdoc}
      class="w-full rounded-lg bg-white"
      style="min-height:140px;border:0;display:block;"
      scrolling="no"
    />
  );
}

/**
 * Modal preview of a nested `message/rfc822`/`.eml` attachment. Signal-driven:
 * open whenever a preview is loading, loaded, or errored; mounted globally in
 * `App`. Reuses the file-local {@link EmailFrame} so the nested body renders in
 * the exact same sandboxed, server-sanitized iframe as top-level messages —
 * there is no second HTML render path. Nested attachments are listed by name
 * only (no download/view — no recursion).
 */
export function EmlPreviewModal(): JSX.Element | null {
  const preview = emlPreview.value;
  const loading = emlPreviewLoading.value;
  const err = emlPreviewError.value;

  // Closed: nothing loading, loaded, or errored.
  if (!preview && !loading && !err) return null;

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-scrim p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeEmlPreview();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Attachment preview"
    >
      <div class="glass flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl shadow-2xl ring-1 ring-strong">
        <div class="flex shrink-0 items-center justify-between gap-3 border-b border-strong px-6 py-4">
          <h2 class="min-w-0 truncate text-base font-semibold text-fg">
            {preview ? preview.subject || "(no subject)" : "Attachment preview"}
          </h2>
          <button class="icon-btn shrink-0" onClick={() => closeEmlPreview()} aria-label="Close">
            <Icon name="x" size={18} />
          </button>
        </div>

        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {loading && <div class="p-6 text-sm text-muted">Loading preview…</div>}

          {err && !loading && (
            <div class="flex items-center gap-2 p-6 text-sm text-danger">
              <Icon name="mailOpen" size={16} />
              <span>{err}</span>
            </div>
          )}

          {preview && !loading && !err && (
            <>
              <div class="shrink-0 border-b border-subtle px-6 py-4 text-sm">
                <div class="text-fg">
                  <span class="text-muted">From: </span>
                  {addrLine(preview.from) || "—"}
                </div>
                <div class="text-fg">
                  <span class="text-muted">To: </span>
                  {addrLine(preview.to) || "—"}
                </div>
                {preview.cc.length > 0 && (
                  <div class="text-fg">
                    <span class="text-muted">Cc: </span>
                    {addrLine(preview.cc)}
                  </div>
                )}
                <div class="mt-0.5 text-xs text-muted">{formatListDate(preview.date)}</div>
              </div>

              <div class="px-6 py-4">
                {preview.html != null ? (
                  <EmailFrame html={preview.html} />
                ) : (
                  <pre class="whitespace-pre-wrap break-words rounded-lg bg-white p-4 text-sm text-black">
                    {preview.text || "(empty message)"}
                  </pre>
                )}
              </div>

              {preview.attachments.length > 0 && (
                <div class="border-t border-strong px-6 py-4">
                  <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                    {preview.attachments.length} nested attachment
                    {preview.attachments.length > 1 ? "s" : ""}
                  </div>
                  <ul class="flex flex-col gap-1">
                    {preview.attachments.map((att, i) => (
                      <li
                        key={`${att.filename}-${i}`}
                        class="flex items-center gap-1.5 text-xs text-fg-dim"
                      >
                        <Icon name="paperclip" size={14} class="text-muted" />
                        <span class="max-w-[20rem] truncate">{att.filename || "(unnamed)"}</span>
                        {att.size > 0 && <span class="text-muted">({formatBytes(att.size)})</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function addrLine(list: MailAddress[]): string {
  return list.map((a) => displayName(a)).join(", ");
}

function formatBytes(n: number): string {
  if (!n) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${units[i] ?? "B"}`;
}

function EmptyReader(): JSX.Element {
  return (
    <section class="flex min-w-0 flex-1 items-center justify-center bg-ink-0">
      <div class="max-w-xs text-center">
        <div class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-ink-2">
          <Icon name="mailOpen" size={40} class="text-ink-4" />
        </div>
        <h3 class="mb-1 text-base font-semibold text-fg-dim">No message selected</h3>
        <p class="text-sm text-muted">
          Pick a message from the list to read it, or press{" "}
          <kbd class="rounded bg-ink-3 px-1.5 py-0.5 text-xs font-mono text-fg-dim">c</kbd>
          {" "}to compose.
        </p>
      </div>
    </section>
  );
}

function ReaderSkeleton(): JSX.Element {
  return (
    <div class="animate-pulse p-6 space-y-4">
      <div class="h-6 w-3/4 rounded bg-ink-3" />
      <div class="flex items-center gap-3">
        <div class="h-10 w-10 rounded-full bg-ink-3 shrink-0" />
        <div class="flex-1 space-y-2">
          <div class="h-3 w-1/3 rounded bg-ink-3" />
          <div class="h-3 w-1/2 rounded bg-ink-2" />
        </div>
      </div>
      <div class="space-y-2 pt-4">
        <div class="h-3 w-full rounded bg-ink-2" />
        <div class="h-3 w-5/6 rounded bg-ink-2" />
        <div class="h-3 w-4/6 rounded bg-ink-2" />
      </div>
    </div>
  );
}
